import { useCallback, useMemo } from 'react';

import { useCausesQueries } from '#pages/Checkout/hooks/useCausesQueries.js';
import { ConsumerCheckoutCartsResponse, useShowConsumerCheckout } from '@givebacks/api/store';
import { arrayHasLength } from '@givebacks/utils/array';
import { formatList } from '@givebacks/utils/strings';
import { LEGAL_URLS } from 'core/Utils';
import { Anchor, Card, Text } from 'ui';

const CheckoutGivingaLegalText = ({ causeNames }: { causeNames?: string }) => {
  return (
    <Card bg="gray.0" radius="sm">
      <Text fz={12}>
        You&apos;re donating to{' '}
        <Text span fz={12} fw={600}>
          {causeNames}
        </Text>{' '}
        via the Givinga Foundation, a Donor Advised Fund (DAF) that helps facilitate the
        disbursement of your donation.{' '}
        <Anchor href={LEGAL_URLS.TAX_DEDUCTIBILITY_URL} target="_blank" fz={12} fw={500}>
          Your contribution may not be deductible.
        </Anchor>{' '}
        Donated funds will be disbursed by The Givinga Foundation{' '}
        <Anchor href={LEGAL_URLS.TERMS_OF_SERVICE_URL} target="_blank" fz={12} fw={500}>
          within 45 days of processing.
        </Anchor>{' '}
        See{' '}
        <Anchor href={LEGAL_URLS.TERMS_OF_SERVICE_URL} target="_blank" fz={12} fw={500}>
          Terms of Service
        </Anchor>{' '}
        for details.
      </Text>
    </Card>
  );
};

const CheckoutGivingaLegal = ({ checkoutId }: { checkoutId: string }) => {
  const { data: causeIds } = useShowConsumerCheckout({
    checkoutId,
    select: useCallback(
      (data: ConsumerCheckoutCartsResponse) =>
        data.checkout.carts
          .filter(({ processed_via }) => Boolean(processed_via === 'givinga'))
          .map(({ cause_id }) => cause_id),
      []
    ),
  });

  const { causeNames } = useCausesQueries(causeIds);

  const causeNamesString = useMemo(() => {
    if (!causeNames.length) return '';
    return formatList(causeNames);
  }, [causeNames]);

  if (!arrayHasLength(causeIds)) return null;
  return <CheckoutGivingaLegalText causeNames={causeNamesString} />;
};

export { CheckoutGivingaLegal };
